<template>
  <div class="h-100 d-flex flex-column">
    <b-row v-if="title" class="mb-3">
      <b-col class="text-left">
        <h4>{{ title }}</h4>
      </b-col>
    </b-row>
    <CustomTable
      id="table"
      class="p-0 text-left flex-grow-1"
      :items="items"
      :fields="fields"
      :filterable="true"
      :selectable="true"
      :current-page="currentPage"
      :per-page="perPage"
      :total-rows="totalRows"
      :stickyHeader="stickyHeader"
      @sort-changed="onSortChanged"
      @filtered="onFilterChanged"
      @page-changed="onPageChanged"
      @per-page-changed="onPerPageChanged"
    >
      <template #empty>
        <h4 class="text-center mt-3">Aucune résidence presente</h4>
      </template>
      <template v-slot:cell(actions)="data">
        <div class="text-center">
          <a
            href="#"
            title="Edit"
            :data-cy="`view-residence-content-${data.item._id}`"
            @click="showResidenceContentModal(data.item)"
          >
            <i class="fas fa-edit" /> </a
          >&nbsp;
          <a
            href="#"
            title="Delete"
            :data-cy="`delete-residence-${data.item._id}`"
            @click="deleteResidence(data.item)"
          >
            <i class="fas fa-trash" />
          </a>
          &nbsp;
        </div>
      </template>
    </CustomTable>

    <b-modal
      v-if="residence"
      id="residency-modal"
      :title="`${residence._id ? 'Editer' : 'Créer'} une résidence`"
    >
      <b-form>
        <b-form-group label="Résidence" label-for="name">
          <b-form-input
            id="name"
            v-model="residence.model"
            data-cy="new-name-modal-name-input"
            placeholder="Nom de la résidence"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group label="SystemId" label-for="systemId">
          <b-form-input
            id="systemId"
            v-model="residence.reference"
            data-cy="new-systemId-modal-name-input"
            placeholder="Numéro d'ID de système"
            required
          ></b-form-input>
        </b-form-group>
        <b-form-group
          label="Lien de contrôle à distance"
          label-for="remoteCtrlLink"
        >
          <b-form-input
            id="remoteCtrlLink"
            v-model="residence.metadata.remoteCtrlLink"
            data-cy="new-systemId-modal-name-input"
            placeholder="Lien de contrôle à distance"
          ></b-form-input>
        </b-form-group>
      </b-form>
      <template #modal-footer>
        <b-button variant="secondary" @click="onClose"> Annuler </b-button>
        <b-button
          type="button"
          variant="primary"
          data-cy="residency-modal-submit-btn"
          @click="onSubmitCreateEdit"
          >{{ `${residence._id ? 'Sauvegarder' : 'Créer'}` }}</b-button
        >
      </template>
    </b-modal>

    <b-modal id="delete-residency-modal" size="l">
      <p v-if="residence && residence.model">
        Voulez-vous supprimer cette résidence ?<code>
          {{ residence.model }}</code
        >
      </p>
      <template #modal-footer>
        <div class="w-100">
          <b-button
            variant="primary"
            class="float-right mr-3"
            @click="$bvModal.hide('delete-residency-modal')"
          >
            Non
          </b-button>
          <b-button
            variant="primary"
            class="float-right mr-3"
            @click="onSubmitDelete"
          >
            Oui
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
require('ace-builds');
require('ace-builds/webpack-resolver');

import { ref, reactive, watch } from '@vue/composition-api';

import { mapGetters, mapActions } from 'vuex';
import { getMeasureByType } from '@/helpers/measures';
import CustomTable from '@/components/CustomTable.vue';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import omit from 'lodash/omit';

export default {
  name: 'TableView',
  components: {
    CustomTable,
  },
  props: {
    index: {
      type: String,
      required: true,
    },
    controller: {
      type: String,
      required: true,
    },
    collection: {
      type: String,
      required: true,
    },
    addItemLabel: {
      type: String,
      required: false,
      default: 'Add an item',
    },
    title: {
      type: String,
      required: false,
    },
    includeGlobalIndex: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideCreateAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideEditAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDeleteAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    showDetachAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataType: {
      type: String,
      required: true,
      default: 'asset',
      validator: (val) => ['asset', 'device'].includes(val),
    },
    showMapAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    showOnMapAction: {
      type: Boolean,
      required: false,
      default: false,
    },
    stickyHeader: {
      required: false,
      default: true,
    },
  },
  data() {
    return {
      residence: this.createResidenceState(),
    };
  },
  computed: {
    ...mapGetters('tenants', ['tenants', 'selectedTenants']),
  },
  methods: {
    ...mapActions('tenants', ['FETCH_TENANT_LIST', 'FETCH_USER_TENANTS']),
    deleteResidence(data) {
      this.residence = this.createResidenceState(data);
      this.$bvModal.show('delete-residency-modal');
    },
    showResidenceContentModal(data) {
      this.residence = this.createResidenceState(data);
      this.$bvModal.show('residency-modal');
    },
    dispatchFetchTenantsAction() {
      this.FETCH_TENANT_LIST(this.$kuzzle);
    },
    createResidence() {
      this.residence = this.createResidenceState();
      this.$bvModal.show('residency-modal');
    },
    createResidenceState(item) {
      return {
        _id: get(item, '_id', null),
        model: get(item, 'model', null),
        reference: get(item, 'reference', null),
        type: 'residence',
        metadata: {
          remoteCtrlLink: get(item, 'metadata.remoteCtrlLink', null),
        },
      };
    },
    async onSubmitCreateEdit() {
      try {
        await this.$kuzzle.query(
          {
            index: this.index,
            controller: 'pvstudio',
            action: this.residence._id ? 'updateResidence' : 'createResidence',
            _id: this.residence._id,
            body: omit(this.residence, ['_id']),
          },
          {
            refresh: 'wait_for',
          }
        );
        this.dispatchFetchTenantsAction();
        this.residence = this.createResidenceState();
        this.$bvModal.hide('residency-modal');
      } catch (error) {
        this.$log.error(error);
        this.$bvToast.toast(
          `Désolé, une erreur est survenue lors de ${
            this.residence._id ? "l'édition" : 'la création'
          } de la résidence.`,
          {
            title: 'Erreur',
            autoHideDelay: 5000,
            variant: 'danger',
            appendToast: true,
          }
        );
      }
      await this.fetchItems();
    },
  },
  setup(props, ctx) {
    const createUpdateModalUid = Date.now().toString();
    const attachModalUid = (Date.now() + Math.random()).toString();
    const items = reactive([]);
    let totalRows = ref(0);
    let currentPage = 1;
    let perPage = ref(10);
    let filter = ref('');
    let sort = ref(null);
    let formSchema = ref({});
    let fields = ref([]);
    let document = ref({});
    let modalAction = ref('');
    let attachToTenant = ref('');
    let name = ref(null);
    let systemId = ref(null);
    let isNameValid = ref(null);

    const fetchItems = async (bounds) => {
      try {
        let query = {};
        if (filter.value.length) {
          query = {
            query: {
              bool: {
                should: [
                  {
                    bool: {
                      must: {
                        multi_match: {
                          query: filter.value,
                          type: 'phrase_prefix',
                          fields: ['model.text', 'reference.text', 'type.text'],
                        },
                      },
                    },
                  },
                ],
              },
            },
          };
        } else {
          query.query = {
            bool: { should: [{ bool: {} }] },
          };
        }

        if (sort.value) {
          query.sort = sort.value;
        } else {
          query.sort = {
            '_kuzzle_info.createdAt': 'desc',
          };
        }

        if (props.includeGlobalIndex) {
          query.query.bool.should[0].bool.must_not = {
            exists: {
              field: 'tenantId',
            },
          };
        }
        if (bounds) {
          query.query.bool.filter = {
            geo_bounding_box: {
              ['measures.position.point']: {
                top_left: {
                  lat: bounds._northEast.lat,
                  lon: bounds._southWest.lng,
                },
                bottom_right: {
                  lat: bounds._southWest.lat,
                  lon: bounds._northEast.lng,
                },
              },
            },
          };
        }
        const res = await ctx.root.$kuzzle.query({
          controller: props.includeGlobalIndex ? 'document' : props.controller,
          action: 'search',
          collection: props.collection,
          index: props.index,
          body: {
            query: query.query,
            sort: query.sort,
          },
          from: (currentPage - 1) * perPage.value,
          size: perPage.value,
        });

        let result = res.result;

        items.splice(items, items.length);
        for (const doc of result.hits) {
          items.push({
            _id: doc._id,
            ...doc._source,
          });
        }
        ctx.emit('fetched', items);
        totalRows.value = result.total;

        const mappings = await ctx.root.$kuzzle.collection.getMapping(
          props.index,
          props.collection,
          {
            includeKuzzleMeta: false,
          }
        );
        fields.value.push(
          {
            key: 'model',
            label: 'Nom',
            sortable: true,
            thClass: 'bg-success text-light',
          },
          {
            key: 'reference',
            label: 'SystemId',
            sortable: true,
            thClass: 'bg-success text-light',
          },
          {
            key: 'actions',
            label: '',
            sortable: false,
            thClass: 'bg-success text-light',
          }
        );
      } catch (error) {
        ctx.root.$log.error(error);
        ctx.root.$bvToast.toast(
          'Désolé, une erreur est survenue lors de la récupération de la liste des résidences.',
          {
            title: 'Erreur',
            autoHideDelay: 5000,
            variant: 'danger',
            appendToast: true,
          }
        );
      }
    };

    watch(
      () => [props.index, props.collection],
      () => fetchItems()
    );

    fetchItems();

    return {
      get,
      createUpdateModalUid,
      getMeasureByType,
      attachModalUid,
      attachToTenant,
      items,
      currentPage,
      perPage,
      totalRows,
      fields,
      formSchema,
      document,
      modalAction,
      defaultTo,
      fetchItems,
      name,
      systemId,
      isNameValid,
      onPageChanged: (page) => {
        currentPage = page;
        fetchItems();
      },
      onPerPageChanged: (value) => {
        perPage.value = value;
        currentPage = 1;
        fetchItems();
      },
      onFilterChanged: (receivedFilter) => {
        filter.value = receivedFilter;
        fetchItems();
      },
      onSortChanged: (data) => {
        sort.value = [
          {
            [data.sortBy]: {
              order: data.sortDesc ? 'desc' : 'asc',
            },
          },
        ];
        fetchItems();
      },
      async onSubmitDelete() {
        try {
          await ctx.root.$kuzzle.query(
            {
              index: props.index,
              controller: 'pvstudio',
              action: 'deleteResidence',
              _id: this.residence._id,
            },
            {
              refresh: 'wait_for',
            }
          );
          this.dispatchFetchTenantsAction();
          this.$bvModal.hide('delete-residency-modal');
        } catch (e) {
          ctx.root.$log.error(e);
          ctx.root.$bvToast.toast(
            'Désolé, une erreur est survenue lors de la suppression de la résidence.',
            {
              title: 'Erreur',
              autoHideDelay: 5000,
              variant: 'danger',
              appendToast: true,
            }
          );
        }
        await fetchItems();
      },
      validateNameEdit() {
        this.isNameValid =
          this.residence.item.model && this.residence.item.reference
            ? true
            : false;
      },
      validateName() {
        this.isNameValid = this.name && this.systemId ? true : false;
      },
      onClose() {
        this.$bvModal.hide('residency-modal');
        this.isNameValid = null;
        this.name = null;
        this.systemId = null;
      },
    };
  },
};
</script>

<style lang="sass" scoped>
.pointer
  cursor: pointer

.actionsContainer
  width: 80px
  a
    margin-right: 5px
</style>
